/**
 * Phase 20 / Auth — Enterprise Forgot Password Dedicated Page
 * Fully responsive to Theme (defaults to Light mode, switches to Dark mode).
 * Multi-step recovery flow: 1. Email -> 2. OTP Verification -> 3. Change Password -> 4. Return to Login.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  RotateCcw,
  Leaf,
  Sun,
  Moon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/constants/routes';
import { useTheme } from '@/context/ThemeContext';

type Step = 'EMAIL' | 'OTP' | 'NEW_PASSWORD' | 'SUCCESS';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { resolvedTheme, setThemeMode } = useTheme();

  const handleToggleTheme = () => {
    setThemeMode(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const [step, setStep] = useState<Step>('EMAIL');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Step 1: Submit Email
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !email.includes('@')) {
      setError('Please enter a valid enterprise email address.');
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('OTP');
    }, 450);
  };

  // Step 2: Submit OTP (Any demo/fake OTP accepted)
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const cleanedOtp = otp.trim();
    if (cleanedOtp.length < 4) {
      setError('Please enter the 6-digit verification code (e.g. 123456).');
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('NEW_PASSWORD');
    }, 450);
  };

  // Step 3: Change Password
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('SUCCESS');
    }, 550);
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background p-4 sm:p-6 font-heading select-none relative overflow-hidden text-foreground">
      {/* Ambient Decorative Accents */}
      <div className="absolute -top-36 -left-36 h-96 w-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-36 -right-36 h-96 w-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

      {/* Top Header Bar: Logo & Theme Switcher Icon */}
      <div className="flex items-center justify-between w-full max-w-md mb-6 relative z-10">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white shadow-md">
            <Leaf className="h-6 w-6 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-black tracking-tight text-foreground">ProcureAI</span>
            <span className="text-[10px] font-extrabold tracking-widest uppercase text-primary">
              Enterprise Core
            </span>
          </div>
        </div>

        {/* Theme Switcher Icon — Sun when Light mode is active, Moon when Dark mode is active */}
        <button
          type="button"
          onClick={handleToggleTheme}
          aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
          title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
          className="p-2.5 rounded-xl border border-border bg-muted/50 hover:bg-muted text-foreground transition-all hover:border-primary/50 shadow-2xs"
        >
          {resolvedTheme === 'dark' ? (
            <Moon className="h-4.5 w-4.5 text-primary" />
          ) : (
            <Sun className="h-4.5 w-4.5 text-amber-500" />
          )}
        </button>
      </div>

      {/* Box Modal Container — Properly Proportioned & Styled to Active Theme */}
      <div className="w-full max-w-md bg-card border border-border rounded-3xl p-6 sm:p-8 shadow-2xl relative z-10 space-y-6">
        {/* Step Indicator Breadcrumb Pills */}
        <div className="flex items-center justify-between pb-3 border-b border-border/60 text-[11px] font-extrabold uppercase tracking-wider">
          <div className="flex items-center gap-1.5">
            <span
              className={`h-6 px-2.5 rounded-full flex items-center justify-center transition-colors ${
                step === 'EMAIL'
                  ? 'bg-gradient-to-r from-[#3d5ad6] to-[#4767f0] text-white shadow-xs'
                  : 'bg-muted text-foreground-muted'
              }`}
            >
              1. Email
            </span>
            <span className="text-foreground-muted">→</span>
            <span
              className={`h-6 px-2.5 rounded-full flex items-center justify-center transition-colors ${
                step === 'OTP'
                  ? 'bg-gradient-to-r from-[#3d5ad6] to-[#4767f0] text-white shadow-xs'
                  : 'bg-muted text-foreground-muted'
              }`}
            >
              2. OTP
            </span>
            <span className="text-foreground-muted">→</span>
            <span
              className={`h-6 px-2.5 rounded-full flex items-center justify-center transition-colors ${
                step === 'NEW_PASSWORD' || step === 'SUCCESS'
                  ? 'bg-gradient-to-r from-[#3d5ad6] to-[#4767f0] text-white shadow-xs'
                  : 'bg-muted text-foreground-muted'
              }`}
            >
              3. Password
            </span>
          </div>

          <button
            type="button"
            onClick={() => navigate(ROUTES.AUTH.LOGIN)}
            className="text-foreground-secondary hover:text-foreground transition-colors text-xs font-bold"
            title="Cancel and return to sign in"
          >
            Cancel
          </button>
        </div>

        {/* Error Alert Box */}
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-xs font-bold text-rose-600 dark:text-rose-400 animate-in fade-in">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* STEP 1: ENTER EMAIL */}
        {step === 'EMAIL' && (
          <div className="space-y-5 animate-in fade-in slide-in-from-left-2 duration-300">
            <div className="text-center space-y-2">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white flex items-center justify-center shadow-md">
                <Mail className="h-6 w-6" />
              </div>
              <h1 className="text-2xl font-black tracking-tight text-foreground">Forgot Password</h1>
              <p className="text-xs text-foreground-secondary font-medium leading-relaxed">
                Enter your corporate email address to receive a 6-digit verification code.
              </p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Corporate Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted pointer-events-none" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="alexander.hamilton@procureai.enterprise"
                    className="w-full rounded-xl border-2 border-input bg-surface pl-10 pr-3.5 py-2.5 text-sm font-semibold text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-sm font-extrabold bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white shadow-md hover:shadow-indigo-500/30 hover:brightness-110 border-0 flex items-center justify-center gap-2"
                isLoading={isLoading}
              >
                <span>Send Verification Code</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>

            <div className="text-center pt-2 border-t border-border/40">
              <button
                type="button"
                onClick={() => navigate(ROUTES.AUTH.LOGIN)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to Sign In
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: ENTER OTP */}
        {step === 'OTP' && (
          <div className="space-y-5 animate-in fade-in slide-in-from-right-2 duration-300">
            <div className="text-center space-y-2">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white flex items-center justify-center shadow-md">
                <KeyRound className="h-6 w-6" />
              </div>
              <h1 className="text-2xl font-black tracking-tight text-foreground">Verify OTP Code</h1>
              <p className="text-xs text-foreground-secondary font-medium leading-relaxed">
                We've sent a verification code to <strong className="text-foreground">{email}</strong>.
              </p>
            </div>

            {/* Demo / Fake Hint Pill */}
            <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-center text-xs font-bold text-primary">
              Demo Code: Enter any 6 digits (e.g. <span className="font-mono font-black">123456</span>)
            </div>

            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">6-Digit Verification Code</label>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  className="w-full text-center tracking-[0.35em] font-mono text-xl font-black rounded-xl border-2 border-input bg-surface py-2.5 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-sm font-extrabold bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white shadow-md hover:shadow-indigo-500/30 hover:brightness-110 border-0 flex items-center justify-center gap-2"
                isLoading={isLoading}
              >
                <ShieldCheck className="h-4 w-4" />
                <span>Verify Code & Proceed</span>
              </Button>
            </form>

            <div className="flex items-center justify-between text-xs pt-2 border-t border-border/40">
              <button
                type="button"
                onClick={() => setStep('EMAIL')}
                className="inline-flex items-center gap-1 font-bold text-foreground-secondary hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to Email
              </button>

              <button
                type="button"
                onClick={() => setOtp('123456')}
                className="inline-flex items-center gap-1 font-bold text-primary hover:underline transition-colors"
              >
                <RotateCcw className="h-3 w-3" /> Auto-fill Demo
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: CHANGE PASSWORD */}
        {step === 'NEW_PASSWORD' && (
          <div className="space-y-5 animate-in fade-in slide-in-from-right-2 duration-300">
            <div className="text-center space-y-2">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white flex items-center justify-center shadow-md">
                <Lock className="h-6 w-6" />
              </div>
              <h1 className="text-2xl font-black tracking-tight text-foreground">Set New Password</h1>
              <p className="text-xs text-foreground-secondary font-medium leading-relaxed">
                Create a secure new password for your ProcureAI account.
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* New Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted pointer-events-none" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full rounded-xl border-2 border-input bg-surface pl-10 pr-10 py-2.5 text-sm font-semibold text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors p-1"
                    title={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted pointer-events-none" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full rounded-xl border-2 border-input bg-surface pl-10 pr-10 py-2.5 text-sm font-semibold text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors p-1"
                    title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-sm font-extrabold bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white shadow-md hover:shadow-indigo-500/30 hover:brightness-110 border-0 flex items-center justify-center gap-2"
                isLoading={isLoading}
              >
                <CheckCircle2 className="h-4 w-4" />
                <span>Complete Change & Sign In</span>
              </Button>
            </form>

            <div className="text-center pt-2 border-t border-border/40">
              <button
                type="button"
                onClick={() => setStep('OTP')}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground-secondary hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to OTP Verification
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: SUCCESS */}
        {step === 'SUCCESS' && (
          <div className="space-y-5 text-center animate-in zoom-in-95 duration-300">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border-2 border-emerald-500/30">
              <CheckCircle2 className="h-8 w-8" />
            </div>

            <div className="space-y-1.5">
              <h1 className="text-2xl font-black tracking-tight text-foreground">Password Changed!</h1>
              <p className="text-xs text-foreground-secondary font-medium leading-relaxed">
                Your password has been successfully updated. You can now access your account with your new credentials.
              </p>
            </div>

            <Button
              onClick={() => navigate(ROUTES.AUTH.LOGIN)}
              className="w-full h-12 rounded-xl text-sm font-extrabold bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white shadow-md hover:shadow-indigo-500/30 hover:brightness-110 border-0 flex items-center justify-center gap-2"
            >
              <span>Proceed to Login</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
